import { ConversationProvider } from '@elevenlabs/react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { CoachMark } from './components/CoachMark';
import { ConsentGate } from './components/ConsentGate';
import { FrontDoorCards } from './components/FrontDoorCards';
import { Onboarding } from './components/Onboarding';
import { SignIn } from './components/SignIn';
import { Goodbye } from './components/Goodbye';
import { TabBar } from './components/TabBar';
import { isOnboardingPreview } from './lib/frontDoorCards';
import { shouldRenderHomeWithFrontDoor } from './lib/homeRouteGate';
import { shouldRenderGoodbye } from './lib/goodbyeRoute';
import { resolveLegalDocRoute } from './lib/legalRoute';
import { useAuth } from './lib/useAuth';
import { useConsentGate } from './lib/useConsentGate';
import { useFrontDoorGate } from './lib/useFrontDoorGate';
import { useOnboardingGate } from './lib/useOnboardingGate';
import { useStoryTour } from './lib/useStoryTour';
import { Home } from './pages/Home';
import { Interview } from './pages/Interview';
import { JobDetail } from './pages/JobDetail';
import { Jobs } from './pages/Jobs';
import { MarketingDrafts } from './pages/MarketingDrafts';
import { Privacy } from './pages/Privacy';
import { Profiles } from './pages/Profiles';
import { Refine } from './pages/Refine';
import { Settings } from './pages/Settings';
import { StyleGuide } from './pages/StyleGuide';
import { Terms } from './pages/Terms';
import { Tracker } from './pages/Tracker';

/**
 * Root app — REWORKED to docs/diagrams/c3-onboarding-2026-07-04.svg's full
 * flow (this file previously held B-1's placeholder localStorage-only
 * obShow/ONBOARDING_SEEN_KEY 3-step machine; that has been replaced by
 * onboardingStep.ts's corpus-state -> step function via useOnboardingGate).
 *
 * Node 0 (landing -> sign-in) lives HERE, not inside the Onboarding overlay:
 * a signed-out stranger renders SignIn instead of AppShell/the tab bar
 * entirely (rather than an authenticated shell with an overlay on top) —
 * this matches what the backend already enforces (every corpus/presence/
 * watchlist route is requireAuth-gated; a pre-auth GET /corpus/roles would
 * just 401) and means the demo-card step (which explicitly needs ZERO
 * backend call) is the very first thing a signed-in-but-corpus-empty user
 * sees, with no flash of a shell that isn't ready to render real data yet.
 *
 * useOnboardingGate is only mounted (its corpus read only fires) once a user
 * is signed in — see the `enabled` argument below — so a stranger who hasn't
 * authenticated yet never triggers a 401 in the background.
 *
 * Wave-1 C1 (docs/DEMO-WALKTHROUGH-issues.md #51): the onboarding overlay was
 * previously a purely VISUAL cover — AppShell renders `children` (this file's
 * <Routes>) into .scrollArea and `overlay` as an absolutely-positioned
 * sibling on top (see AppShell.tsx's own doc comment), so Home ALWAYS
 * mounted underneath at "/" regardless of onboarding step, relying entirely
 * on the overlay's CSS layering to hide it. Live evidence: a net-new,
 * 0-corpus account reached the fully-rendered placeholder Home instead of
 * being routed into onboarding. The fix is at the ROUTE, not just the visual
 * layer: the "/" route itself now renders nothing (rather than <Home />)
 * whenever a 0-corpus user is still mid-onboarding — see homeRouteGate.ts's
 * shouldRenderHome (extracted as its own pure, unit-tested function, same
 * "the decision is a pure module" house pattern onboardingStep.ts/
 * jobBuckets.ts/dragRehome.ts already establish — this repo has no jsdom/
 * React-Testing-Library setup, so a routing decision left inline in App.tsx's
 * JSX would be untestable). This is deliberately narrow: it does NOT stop the
 * onboarding flow's own hand-offs into REAL /refine (step 3, "Keep going with
 * Meira") and /profiles (step 4, "Generate my real Get Found profile") from
 * mounting the genuine Refine/Profiles pages underneath the overlay at those
 * specific steps —
 * that hand-off is intentional (the diagram's own node 3/4 behavior), and
 * those pages already render their own honest cold/loading states
 * regardless. Only "/" (Home, the final post-onboarding destination) is
 * gated, since Home is the exact screen the DB-verified bug showed
 * fabricated data on.
 *
 * LANE E7-PORT-THROUGHLINE (2026-07-17, docs/diagrams/c4-e7-front-door-
 * 2026-07-17.html): a SECOND, NEW onboarding phase — 7 profile-facts cards
 * (components/FrontDoorCards.tsx, gated by lib/useFrontDoorGate.ts's server-
 * truth eligibleForOnboarding) — now composes AHEAD of the arc this file's
 * header already describes. The two phases share the ONE `overlay` slot
 * (cards first, then the existing arc falls through unmodified once the
 * cards retire) and every gate above (tabBar/coachMark/"/" route) now checks
 * BOTH phases via showAnyOnboardingPhase / shouldRenderHomeWithFrontDoor —
 * see those symbols' own doc comments. onboardingStep.ts/Onboarding.tsx/
 * useOnboardingGate.ts themselves are UNCHANGED: their 5-node sequence stays
 * diagram-pinned exactly as this header already describes it.
 */
export function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  // LANE E7-PORT-THROUGHLINE: his test path — any signed-in account can open
  // ?onboarding=preview directly, regardless of eligibility. Read once per
  // render from the CURRENT location (matches apps/web's own FrontDoor.tsx
  // isOnboardingPreview usage) so it stays correct across navigation.
  const preview = isOnboardingPreview(location.search);
  const gate = useOnboardingGate(auth.user != null);
  // LANE E7-PORT-THROUGHLINE: the 7 profile-facts cards' OWN gate — called
  // unconditionally, ahead of every early return below (rules of hooks),
  // exactly the same discipline useOnboardingGate/useConsentGate already
  // establish. Composes AHEAD of the existing arc (gate above): showFront-
  // DoorCards decides whether App.tsx renders the cards or falls through to
  // the existing <Onboarding> overlay — see this file's own composition
  // comment further down.
  const frontDoorGate = useFrontDoorGate(auth.user != null, preview);
  // ENROLL lane: the consent read only fires once a real session exists
  // (same `enabled` gate useOnboardingGate's own corpus read already uses),
  // called unconditionally ahead of every early return below (rules of
  // hooks) — its internal `enabled` argument is what actually decides
  // whether GET /auth/consents fires, never a conditional hook call.
  const consent = useConsentGate(auth.user != null);
  // LANE STORY-TOUR: called unconditionally, ahead of every early return
  // below (rules of hooks) — its OWN internal auto-start gate (onboardingDone
  // below) is what actually decides whether the tour is eligible to fire,
  // exactly the same discipline useOnboardingGate's corpus read already uses
  // (mounted early, gated internally, never conditionally called). LANE
  // E7-PORT-THROUGHLINE: also gated on !frontDoorGate.showCards — the tour
  // is a POST-onboarding affordance for both phases now, not just the
  // existing arc.
  const tour = useStoryTour(
    gate.corpus.roleCount,
    !frontDoorGate.showCards && gate.step === 'done',
  );

  // Signed out (or auth still resolving) — no tab bar, no routes, no corpus
  // read. auth.loading covers the brief GET /auth/me round trip on first
  // paint; rendering nothing rather than flashing SignIn-then-the-app avoids
  // a visible flicker for an already-signed-in returning user.
  if (auth.loading) return null;
  // ENROLL lane: /terms and /privacy must be reachable REGARDLESS of auth
  // state — a signed-out stranger reads them from SignIn's own footer link
  // before ever clicking "Continue with Google," and a signed-in user reads
  // them from Settings or the consent gate below. Checked before EVERY other
  // gate (goodbye, sign-in, consent) — the same "outranks the rest" shape
  // shouldRenderGoodbye already has, extended to a second always-reachable
  // pair of routes.
  const legalDoc = resolveLegalDocRoute(window.location.pathname);
  if (legalDoc === 'terms') return <Terms />;
  if (legalDoc === 'privacy') return <Privacy />;
  // R207: the goodbye landing outranks the sign-in gate — a fresh sign-out
  // hard-navigates to /goodbye and must SEE it, not get swallowed by SignIn.
  if (shouldRenderGoodbye(window.location.pathname))
    return <Goodbye onSignIn={() => auth.signIn('/')} />;
  if (!auth.user) return <SignIn onSignIn={() => auth.signIn('/')} />;

  // ENROLL lane: the first-run/grandfather consent screen outranks
  // onboarding — a user whose consents are missing/stale sees ONLY this
  // screen next, never a flash of the onboarding overlay or the tab bar
  // underneath it. `consent.statuses === null` (the initial GET still in
  // flight) renders nothing rather than assuming either "needs it" or
  // "doesn't" — the same "unknown, not empty" discipline gate.corpus.roleCount
  // already establishes just below. Declining signs the user out politely
  // (their data stays exactly as it was — signOut touches only the session)
  // rather than trapping them with no way forward.
  if (consent.statuses === null) return null;
  if (consent.statuses.some((s) => s.state !== 'current')) {
    return (
      <ConsentGate
        statuses={consent.statuses}
        phase={consent.phase}
        error={consent.error}
        onAccept={consent.acceptAll}
        onDecline={auth.signOut}
      />
    );
  }

  const showOnboarding = gate.step !== 'done';
  // LANE E7-PORT-THROUGHLINE: the composed "is EITHER onboarding phase
  // showing" flag — drives the tab bar / coach mark exactly like
  // showOnboarding did alone before this lane; see homeElement below for the
  // "/" route's own composed decision.
  const showAnyOnboardingPhase = frontDoorGate.showCards || showOnboarding;
  // Server truth (gate.corpus.roleCount), never a client guess — mirrors
  // hasRealCorpus below. null (still loading / unknown) is treated the SAME
  // as "not yet confirmed empty," so "/" briefly renders nothing rather than
  // a flash of Home while the very first corpus read is still in flight —
  // matches useOnboardingGate's own "unknown, not empty" discipline.
  const hasRealCorpus = (gate.corpus.roleCount ?? 0) > 0;
  // "/" renders Home only once BOTH onboarding phases are done (or the user
  // already has a real corpus) — a returning user with data reaches Home
  // immediately, same as today; only a genuinely 0-corpus, still-onboarding
  // user is gated, now across either phase. shouldRenderHomeWithFrontDoor is
  // a pure OR-composition over the existing, untouched shouldRenderHome.
  const homeElement = shouldRenderHomeWithFrontDoor(frontDoorGate.showCards, gate.step, hasRealCorpus) ? (
    <Home />
  ) : null;

  function handleNavigate(path: '/refine' | '/profiles') {
    navigate(path);
  }

  return (
    // The ElevenLabs ConversationProvider, hoisted here (mirrors apps/web's
    // App.tsx — its own comment explains why: EVERY route gets the ancestor
    // useLivingConversation needs, it is inert when no descendant calls
    // useConversation, and nothing is gated on a flag at this level). Only
    // Refine mounts a live session today; Home/Profiles/Jobs render
    // unaffected.
    <ConversationProvider>
      <AppShell
        tabBar={!showAnyOnboardingPhase ? <TabBar /> : undefined}
        onSignOut={auth.signOut}
        overlay={
          // D20 (2026-07-08): pass the overlay ONLY while onboarding is truly
          // showing — an always-present element kept AppShell's showTrigger
          // guard permanently false, so the nav trigger/top bar never rendered
          // for anyone despite landing twice (W-NAV R220, MOBILE-SHELL).
          // LANE E7-PORT-THROUGHLINE: the two onboarding phases share this
          // ONE overlay slot, cards first — never both mounted at once. Once
          // frontDoorGate.showCards flips false (finale done, or skip-all),
          // this falls through to the EXISTING, unmodified <Onboarding>
          // exactly as it always rendered; a user who already has a real
          // corpus or an already-onboarded_at profile never sees
          // <FrontDoorCards> mount at all (frontDoorGate.showCards starts
          // false for them — see useFrontDoorGate.ts's fail-closed default).
          frontDoorGate.showCards ? (
            <FrontDoorCards
              preview={preview}
              onDone={frontDoorGate.markFinished}
              onCorpusSeeded={gate.refreshCorpus}
            />
          ) : showOnboarding ? (
            <Onboarding
              step={gate.step}
              onAdvance={gate.advance}
              onSkip={gate.skip}
              onNavigate={handleNavigate}
              hasRealCorpus={hasRealCorpus}
            />
          ) : undefined
        }
        coachMark={
          // Only once BOTH onboarding phases are done/skipped — the two
          // first-run systems never show at the same time (see AppShell.tsx's
          // own coachMark doc comment for why this is a separate slot from
          // `overlay`, not folded into it).
          !showAnyOnboardingPhase ? (
            <CoachMark
              step={tour.step}
              currentPath={location.pathname}
              onAdvance={tour.advance}
              onSkip={tour.skip}
            />
          ) : undefined
        }
      >
        <Routes>
          <Route path="/" element={homeElement} />
          <Route path="/refine" element={<Refine />} />
          {/* LANE DNA-BUILD: the turn-based DNA interview's OWN sub-route
              (docs/design/dna-interview-port-2026-07-05.md §2's recommended
              option (a)) — reached via a card on Refine, not a 6th tab. No
              TabBar/nav change; Refine.tsx links here directly. */}
          <Route path="/refine/interview" element={<Interview />} />
          <Route path="/profiles" element={<Profiles />} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/jobs/:jobId" element={<JobDetail />} />
          <Route path="/settings" element={<Settings onShowStoryTour={tour.start} />} />
          <Route path="/track" element={<Tracker />} />
          <Route path="/admin/marketing-drafts" element={<MarketingDrafts />} />
          <Route path="/style-guide" element={<StyleGuide />} />
        </Routes>
      </AppShell>
    </ConversationProvider>
  );
}
